import { motion } from "framer-motion";
import { ArrowUpRight, AlertCircle } from "lucide-react";
import Field from "./Field";

const DAY_WINDOWS = [
    "Today",
    "Tomorrow",
    "This Week",
    "Next Week",
    "Flexible",
];

const TIME_WINDOWS = [
    "Morning (8a–12p)",
    "Afternoon (12p–5p)",
    "Evening (5p–8p)",
    "Anytime",
];

const inputCls =
    "w-full bg-transparent border-b border-[rgba(201,168,106,0.3)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-base py-2.5 px-1 transition-colors duration-300 placeholder:text-[#5e5a52]";

const selectCls =
    "w-full bg-[#0B0B0C] border-b border-[rgba(201,168,106,0.3)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-base py-2.5 px-1 transition-colors duration-300 appearance-none";

export default function CallbackFields({
    form,
    errors,
    status,
    submitError,
    setField,
    onSubmit,
}) {
    return (
        <motion.form
            key="form"
            initial={false}
            onSubmit={onSubmit}
            noValidate
            className="space-y-7"
            data-testid="callback-form"
        >
            {/* Honeypot */}
            <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: "-9999px",
                    width: 1,
                    height: 1,
                    opacity: 0,
                }}
                data-testid="callback-honeypot"
            />

            <p className="text-[#9A958A] text-sm leading-[1.75] italic font-serif">
                Thirty seconds. We&rsquo;ll handle the rest.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <Field
                    label="Full Name"
                    htmlFor="c_full_name"
                    required
                    error={errors.full_name}
                >
                    <input
                        id="c_full_name"
                        type="text"
                        autoComplete="name"
                        value={form.full_name}
                        onChange={(e) =>
                            setField("full_name", e.target.value)
                        }
                        className={inputCls}
                        data-testid="callback-full_name"
                    />
                </Field>
                <Field
                    label="Phone"
                    htmlFor="c_phone"
                    required
                    error={errors.phone}
                >
                    <input
                        id="c_phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        className={inputCls}
                        data-testid="callback-phone"
                    />
                </Field>
            </div>

            <Field
                label="Email"
                htmlFor="c_email"
                hint="Optional"
                error={errors.email}
            >
                <input
                    id="c_email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    className={inputCls}
                    data-testid="callback-email"
                />
            </Field>

            <div>
                <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#9A958A] mb-3">
                    Preferred Window
                    <span
                        aria-hidden="true"
                        className="text-[#C9A86A] ml-1"
                    >
                        *
                    </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <Field
                        label="Day"
                        htmlFor="c_day_window"
                        error={errors.day_window}
                    >
                        <select
                            id="c_day_window"
                            value={form.day_window}
                            onChange={(e) =>
                                setField("day_window", e.target.value)
                            }
                            className={selectCls}
                            data-testid="callback-day_window"
                        >
                            <option value="">Select…</option>
                            {DAY_WINDOWS.map((d) => (
                                <option key={d} value={d}>
                                    {d}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field
                        label="Time"
                        htmlFor="c_time_window"
                        error={errors.time_window}
                    >
                        <select
                            id="c_time_window"
                            value={form.time_window}
                            onChange={(e) =>
                                setField("time_window", e.target.value)
                            }
                            className={selectCls}
                            data-testid="callback-time_window"
                        >
                            <option value="">Select…</option>
                            {TIME_WINDOWS.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
            </div>

            <Field
                label="Brief Note"
                htmlFor="c_note"
                hint="Anything we should know before we call? (Optional)"
            >
                <textarea
                    id="c_note"
                    rows={2}
                    value={form.note}
                    onChange={(e) => setField("note", e.target.value)}
                    className={`${inputCls} resize-y`}
                    data-testid="callback-note"
                />
            </Field>

            <div>
                <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={(e) =>
                            setField("consent", e.target.checked)
                        }
                        className="mt-1 w-4 h-4 accent-[#C9A86A] cursor-pointer"
                        aria-describedby={
                            errors.consent ? "c_consent-error" : undefined
                        }
                        data-testid="callback-consent"
                    />
                    <span className="text-[#9A958A] text-sm leading-[1.7]">
                        I agree to be contacted by phone.
                    </span>
                </label>
                {errors.consent && (
                    <p
                        id="c_consent-error"
                        role="alert"
                        className="mt-2 flex items-center gap-2 text-[12px] text-[#C97A6A]"
                    >
                        <AlertCircle size={12} strokeWidth={1.6} />
                        {errors.consent}
                    </p>
                )}
            </div>

            {submitError && (
                <div
                    role="alert"
                    className="border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A] leading-[1.7]"
                    data-testid="callback-submit-error"
                >
                    {submitError}
                </div>
            )}

            <div className="pt-2">
                <button
                    type="submit"
                    disabled={status === "loading"}
                    className="btn-gold w-full md:w-auto disabled:opacity-70 disabled:cursor-wait"
                    data-testid="callback-submit"
                >
                    {status === "loading" ? (
                        <>
                            <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            Requesting
                        </>
                    ) : (
                        <>
                            Request Callback
                            <ArrowUpRight size={14} strokeWidth={1.4} />
                        </>
                    )}
                </button>
            </div>
        </motion.form>
    );
}
